import classNames from 'classnames'
import { CSSProperties, useCallback, useEffect, useState } from 'react'

import stylesTable from 'core-components/library/list/Table.module.scss'
import { getFilterFields } from 'core-components/library/list/table/thead/helpers/getFilterFields'
import { Tooltip } from 'core-components/library/tooltip'
import { BaseCrud } from 'core-components/types'
import { IDataProviderSort } from 'core-components/types'

import { prepareStickyColumns } from '../helpers/prepareStickyColumns'
import stylesStickyColumns from '../StickyColumns.module.scss'
import styles from '../THead.module.scss'
import { IHierarchyColumn, TTitleRenderFunction } from '../types'

import { getArraySort } from './helpers/getArraySort'
import { ThPopup } from './th/ThPopup'

export interface IThProps<T, R> {
  className?: string
  source: BaseCrud<T, R>
  column: IHierarchyColumn<T>
  columns: IHierarchyColumn<T>[]
  headerLine: number
  headerLinesCount: number
  onPriorityChange: (value: number) => void
  titleRender?: TTitleRenderFunction
  hasHierarchy?: boolean
  additionalStyles?: CSSProperties
}

export function Th<T, R>({
  className,
  source,
  column,
  onPriorityChange,
  titleRender,
  columns,
  headerLine,
  headerLinesCount,
  hasHierarchy,
  additionalStyles,
}: IThProps<T, R>) {
  const [refresh, setRefresh] = useState(1)
  const [isHighlightVisible, setIsHightlightVisible] = useState<boolean>(false)
  const isLastLine = headerLine + (column.rowSpan || 1) === headerLinesCount
  const disableSort = column.disableSort || (headerLinesCount > 1 && !isLastLine)

  const sort = getArraySort(source.combineSort())
  const columnSortSetting = sort.find((el) => el.field === column.field)
  const columnSortIndex = sort.findIndex((el) => el.field === column.field)

  const sortingHandle = useCallback(() => {
    if (disableSort) {
      return
    }

    const firstClickSortDirection = column.sortDescFirst ? 'desc' : 'asc'
    const secondClickSortDirection = firstClickSortDirection === 'asc' ? 'desc' : 'asc'

    if (sort && !!columnSortSetting) {
      if (columnSortSetting.dir === firstClickSortDirection) {
        const newSort: IDataProviderSort[] = sort.map((el) => {
          if (el.field === column.field) {
            return {
              field: column.field,
              dir: secondClickSortDirection,
            }
          }
          return el
        })
        source.setSort(newSort)
      }
      if (columnSortSetting.dir === secondClickSortDirection) {
        const newSort: IDataProviderSort[] = sort.filter(
          (el) => el.field !== columnSortSetting.field
        )
        source.setSort(newSort)
      }
    } else {
      const arraySort = getArraySort(sort)
      source.setSort([
        ...arraySort,
        {
          field: column.field,
          dir: firstClickSortDirection,
        },
      ])
    }
  }, [refresh, disableSort, column.field, columnSortSetting, sort, source])

  const renderedText =
    (titleRender && titleRender('thDisabled', column.title)) ||
    (column.titleRender && column.titleRender({ headerLine, column })) ||
    column.title

  const { leftColumnShift, isLastStickyColumn } = prepareStickyColumns(
    column,
    columns,
    hasHierarchy
  )

  const calculateHighlighted = useCallback(() => {
    const filterFields = getFilterFields(source.getFilter())
    setIsHightlightVisible(filterFields.includes(column.field))
  }, [column.field, source])

  useEffect(() => {
    calculateHighlighted()
    return source.subscribe('change', () => {
      calculateHighlighted()
    })
  }, [calculateHighlighted, source])

  useEffect(() => {
    return (source as BaseCrud<T, R>).subscribe('change', () => {
      setRefresh((val) => val + 1)
    })
  }, [source])

  return (
    <th
      data-testid={`header-cell-${column.field}`}
      colSpan={column.colspan}
      rowSpan={column.rowSpan}
      style={{
        left: column.isStickyColumn ? leftColumnShift : undefined,
        ...(additionalStyles || {}),
      }}
      className={classNames(styles.th, className || '', {
        [stylesStickyColumns.stickyColumn]: column.isStickyColumn,
        [stylesStickyColumns.stickyColumn__lastStickyColumn]: isLastStickyColumn,
      })}
    >
      {titleRender && !!titleRender('thEnabled', column.title) ? (
        <span className={styles['title-translation-mode']}>
          {titleRender('thEnabled', column.title)}
        </span>
      ) : (
        <div className={styles.header}>
          <button
            data-testid={`table-column-th-${column.field}`}
            className={classNames(
              {
                [styles['th__not-last-line']]: headerLinesCount > 1,
              },
              stylesTable.button,
              styles.title,
              disableSort ? styles['title--not-clickable'] : ''
            )}
            onClick={sortingHandle}
            type={'button'}
          >
            {renderedText ? (
              <Tooltip
                className={styles.titleTooltip}
                tipContent={() => <>{renderedText}</>}
                disabled={column.tooltipVisible === false}
              >
                <div className={styles.titleTooltip__inner}>
                  <span
                    className={styles.titleTooltip__text}
                    data-testid={`${column.field}_table_title`}
                  >
                    {renderedText}
                  </span>
                  {!column.hideSortIcon || !column.hideSortIndex || !column.hideFilterIcon ? (
                    <div className={styles.titleTooltip__append}>
                      {!column.hideSortIcon || !column.hideSortIndex ? (
                        <div
                          className={classNames(
                            styles.sort,
                            !columnSortSetting ? 'content-invisible' : ''
                          )}
                        >
                          {!column.hideSortIcon ? (
                            <i
                              data-testid={`table-column-th-${column.field}-sort-icon`}
                              className={classNames(
                                styles.sort__icon,
                                columnSortSetting?.dir === 'asc'
                                  ? 'kbase kbase-arrow-top'
                                  : 'kbase kbase-arrow-bottom'
                              )}
                            />
                          ) : null}
                          {!column.hideSortIndex ? (
                            <span
                              className={styles.sort__index}
                              data-testid={`table-column-th-${column.field}-sort-index`}
                            >
                              {columnSortIndex !== -1 ? columnSortIndex + 1 : null}
                            </span>
                          ) : null}
                        </div>
                      ): null}
                      {!column.hideFilterIcon ? (
                        <div
                          className={classNames(
                            styles.highlighIcon,
                            !isHighlightVisible ? 'content-hidden' : ''
                          )}
                          data-testid={`table-column-th-${column.field}-filter`}
                        >
                          <i className={'kbase kbase-bars-filter'} />
                        </div>
                      ): null}
                    </div>
                  ) : null}
                </div>
              </Tooltip>
            ) : null}
          </button>
          {!column.hideSettings && isLastLine ? (
            <ThPopup
              source={source}
              column={column}
              onPriorityChange={onPriorityChange}
              refresh={refresh}
              titleRender={titleRender}
              headerLine={headerLine}
            />
          ) : null}
        </div>
      )}
    </th>
  )
}
